import { z } from 'zod';
import { INSPIRATION_DIMENSIONS } from './inspiration-registry.js';
export * from './inspiration-registry.js';
const dimensionKeys = new Set<string>(INSPIRATION_DIMENSIONS.map((d) => d.key));
export const inspirationDimensionSchema = z
  .string()
  .min(1)
  .max(100)
  .refine((key) => dimensionKeys.has(key), 'Unregistered inspiration dimension');
const metric = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).nullable();
export const inspirationMetricsSchema = z.object({
  likes: metric,
  views: metric,
  comments: metric,
  saves: metric,
  shares: metric,
  reactions: metric,
});
export const inspirationEvidenceSchema = z.object({
  id: z.string().min(1).max(160),
  kind: z.enum(['note', 'metadata', 'image', 'video', 'audio', 'caption', 'human']),
  locator: z.string().max(2000).optional(),
});
export const inspirationAssertionSchema = z.object({
  dimension: inspirationDimensionSchema,
  value: z.string().min(1).max(160),
  status: z.enum(['observed', 'reported', 'inferred']),
  evidenceIds: z.array(z.string().min(1).max(160)).min(1).max(20),
});
export const inspirationAnalysisSchema = z
  .object({
    version: z.literal(1),
    summary: z.string().max(500),
    source: z.object({
      identityStatus: z.enum(['unverified', 'matched', 'mismatch', 'unavailable']),
      eligibility: z.enum(['guest-stay', 'adjacent', 'excluded', 'unknown']),
      reviewStatus: z.enum(['pending', 'reviewed', 'rejected']),
    }),
    evidence: z.array(inspirationEvidenceSchema).max(100),
    assertions: z.array(inspirationAssertionSchema).max(262),
    metrics: inspirationMetricsSchema,
    issues: z.array(z.string().max(500)).max(40),
    provenance: z.object({
      method: z.enum(['notes', 'model', 'human']),
      model: z.string().max(160).nullable(),
      analyzedAt: z.iso.datetime(),
    }),
  })
  .superRefine((analysis, ctx) => {
    const ids = new Set<string>();
    for (const evidence of analysis.evidence) {
      if (ids.has(evidence.id))
        ctx.addIssue({ code: 'custom', message: 'Duplicate evidence ID', path: ['evidence'] });
      ids.add(evidence.id);
    }
    const pairs = new Set<string>();
    analysis.assertions.forEach((assertion, index) => {
      const pair = JSON.stringify([assertion.dimension, assertion.value]);
      if (pairs.has(pair))
        ctx.addIssue({
          code: 'custom',
          message: 'Duplicate dimension/value assertion',
          path: ['assertions', index],
        });
      pairs.add(pair);
      if (assertion.evidenceIds.some((id) => !ids.has(id)))
        ctx.addIssue({
          code: 'custom',
          message: 'Assertion references missing evidence',
          path: ['assertions', index, 'evidenceIds'],
        });
    });
  });
export type InspirationAnalysis = z.infer<typeof inspirationAnalysisSchema>;
export type InspirationMetrics = z.infer<typeof inspirationMetricsSchema>;
export type InspirationAssertion = z.infer<typeof inspirationAssertionSchema>;
